import jsPDF from 'jspdf';
import { splitMarkdownFrontmatter } from './markdownFrontmatter';

/**
 * Collects all documentation files from the sidebar structure
 */
export const getAllDocsFiles = (modules) => {
  const files = [];
  
  const collectFiles = (items) => {
    if (!items || !Array.isArray(items)) return;
    items.forEach(item => {
      // Skip anchor links (paths with #)
      if (item.path && !item.path.includes('#') && !files.find(f => f.path === item.path)) {
        files.push({
          title: item.title,
          path: item.path
        });
      }
    });
  };

  modules.forEach(module => {
    if (module.items) {
      collectFiles(module.items);
    }
    if (module.submenus) {
      Object.values(module.submenus).forEach(submenuItems => {
        collectFiles(submenuItems);
      });
    }
  });

  return files;
};

/**
 * Fetches content of a single documentation file
 */
const fetchDocContent = async (path) => {
  try {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${path}`);
    }
    return await response.text();
  } catch (error) {
    console.error(`Error fetching ${path}:`, error);
    return `# Error Loading ${path}\n\n${error.message}`;
  }
};

/**
 * Fetches all documentation files content
 */
export const fetchAllDocsContent = async (files) => {
  const docs = [];
  
  for (const file of files) {
    const raw = await fetchDocContent(file.path);
    const { body, title } = splitMarkdownFrontmatter(raw);
    docs.push({
      title: file.title || title || file.path,
      path: file.path,
      content: body
    });
  }
  
  return docs;
};

/**
 * Exports all documentation as JSON
 */
export const exportAsJSON = async (modules) => {
  const files = getAllDocsFiles(modules);
  const docs = await fetchAllDocsContent(files);
  
  const jsonData = {
    exportDate: new Date().toISOString(),
    version: '1.0',
    totalDocs: docs.length,
    documentation: docs.map(doc => ({
      title: doc.title,
      path: doc.path,
      content: doc.content
    }))
  };
  
  const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `promang-docs-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Exports all documentation as PDF
 */
export const exportAsPDF = async (modules) => {
  const files = getAllDocsFiles(modules);
  const docs = await fetchAllDocsContent(files);
  
  // Create a new PDF document
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const maxWidth = pageWidth - (margin * 2);
  let yPosition = margin;
  const lineHeight = 7;
  const pageBreak = pageHeight - margin;
  
  // Helper function to add a new page if needed
  const checkPageBreak = (requiredSpace = lineHeight) => {
    if (yPosition + requiredSpace > pageBreak) {
      pdf.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };
  
  // Helper function to add text with word wrapping
  const addText = (text, fontSize = 12, isBold = false) => {
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
    
    const lines = pdf.splitTextToSize(text, maxWidth);
    
    lines.forEach((line, index) => {
      checkPageBreak(lineHeight * (fontSize / 12));
      pdf.text(line, margin, yPosition);
      yPosition += lineHeight * (fontSize / 12);
    });
  };
  
  // Add title page
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Promang Documentation', pageWidth / 2, 50, { align: 'center' });
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Exported on: ${new Date().toLocaleDateString()}`, pageWidth / 2, 70, { align: 'center' });
  pdf.text(`Total Documents: ${docs.length}`, pageWidth / 2, 80, { align: 'center' });
  
  yPosition = 120;
  
  // Add table of contents
  addText('Table of Contents', 16, true);
  yPosition += 5;
  
  docs.forEach((doc, index) => {
    checkPageBreak();
    addText(`${index + 1}. ${doc.title}`, 10);
  });
  
  // Add each document
  docs.forEach((doc, docIndex) => {
    // Add new page for each document
    if (docIndex > 0 || yPosition > 100) {
      pdf.addPage();
      yPosition = margin;
    }
    
    // Document title
    addText(doc.title, 18, true);
    yPosition += 5;
    
    // Document path
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);
    addText(`Path: ${doc.path}`, 8);
    pdf.setTextColor(0, 0, 0);
    yPosition += 5;
    
    // Convert markdown to plain text (simplified)
    let text = doc.content;
    
    // Remove markdown headers but keep text
    text = text.replace(/^#+\s+/gm, '');
    
    // Remove markdown links but keep text
    text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    
    // Remove markdown code blocks
    text = text.replace(/```[\s\S]*?```/g, '');
    text = text.replace(/`([^`]+)`/g, '$1');
    
    // Remove markdown bold/italic
    text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
    text = text.replace(/\*([^*]+)\*/g, '$1');
    
    // Split into paragraphs
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim().length > 0);
    
    paragraphs.forEach(paragraph => {
      checkPageBreak(lineHeight * 2);
      addText(paragraph.trim(), 10);
      yPosition += 3; // Space between paragraphs
    });
  });
  
  // Save the PDF
  pdf.save(`promang-docs-${new Date().toISOString().split('T')[0]}.pdf`);
};

